import { FC } from 'react';
import { isBloggerBusiness } from '../../../utils/accessService';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { getDomainFromUrl } from '../../../utils/getDomainFromUrl/getDomainFromUrl';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { CHECKOUT_BUTTON_TEXT } from '../../constants';
import { IconArrowDiagonalRight } from '../../icon/IconArrowDiagonalRight';
import { IconProduct } from '../../icon/IconProduct';
import { IconProductOutline } from '../../icon/IconProductOutline';
import { IconPurchases16 } from '../../icon/IconPurchases16';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Image } from '../../media/Image';
import { PostTextSiteLink } from '../../posts/PostDetailsCard/parts/PostTextSiteLink/PostTextSiteLink';
import { ProductMonetization } from '../../posts/ProductMonetization/ProductMonetization';
import { Price } from '../../price/Price';
import { Typography } from '../../typography/Typography';
import { ProductDomain } from '../ProductDomain/ProductDomain';
import {
    ChevronMultiple,
    Content,
    ExpandableButton,
    ExpandableButtonText,
    ExpandableButtonWrapper,
    ImageWrapper,
    OverlayLink,
    RelativeContainer,
} from './styles';
import { ProductSnippetProps } from './types';

export const ProductSnippet: FC<ProductSnippetProps> = ({
    product,
    onClick,
    onCheckoutClick,
    showSiteButton,
    post,
    siteButtonText,
    isMultipleProducts,
}) => {
    const t = useTranslation();

    const media = product.content[0]?.media;

    const checkoutFlagValue = Number(useExperimentFlag('products_checkout'));
    const withCheckout =
        (checkoutFlagValue === 1 || checkoutFlagValue === 2) && product.checkout?.url;

    const domain = isBloggerBusiness(post?.business?.type)
        ? getDomainFromUrl(product.originalUrl)
        : undefined;

    const imageStub = siteButtonText ? (
        <IconProductOutline size={24} color="var(--color-primary)" margin="auto 0 auto 12px" />
    ) : (
        <ImageWrapper isMultipleProducts={isMultipleProducts}>
            <IconProduct size={36} color="var(--control-secondary-background)" margin="auto" />
        </ImageWrapper>
    );

    const onCheckoutClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();

        onCheckoutClick?.();
        product.checkout?.url && window.open(product.checkout?.url, '_blank');
    };

    if (!product) {
        return null;
    }

    return (
        <OverlayLink
            to={product.url}
            onClick={onClick}
            withSiteButton={showSiteButton}
            isHoverEnabled={!withCheckout}
        >
            <Flex
                direction="column"
                width="100%"
                height="100%"
                p={isMultipleProducts ? 0.5 : 1}
                spacing={1}
            >
                <Flex direction="row" width="100%" height="100%" alignItems="center">
                    {media ? (
                        <ImageWrapper isMultipleProducts={isMultipleProducts}>
                            <Image
                                objectFit="cover"
                                width="100%"
                                height="100%"
                                src={buildImageSource(media)}
                                srcSet={buildImageSourceSet(media)}
                            />
                        </ImageWrapper>
                    ) : (
                        imageStub
                    )}
                    <Content isMultipleProducts={isMultipleProducts}>
                        {product.monetizationEnabled ? (
                            <ProductMonetization color="invert" pb={0.5} />
                        ) : null}
                        <Typography variant="body2" loose lines={isMultipleProducts ? 1 : 2}>
                            {product.title}
                        </Typography>
                        <Price
                            priceInfo={product.priceInfo}
                            oldPriceInfo={product.oldPriceInfo}
                            fintechDiscount={product.fintechDiscount}
                            variant="body"
                            oldPriceOptions={{ variant: 'caption' }}
                            showOldPriceBadge
                        />
                        {domain ? <ProductDomain domain={domain} mt={0.5} /> : null}
                    </Content>
                    {isMultipleProducts && !withCheckout ? (
                        <ChevronMultiple color="invert">
                            <IconArrowDiagonalRight size={16} />
                        </ChevronMultiple>
                    ) : null}
                    {isMultipleProducts && withCheckout ? (
                        <RelativeContainer>
                            <ExpandableButtonWrapper>
                                <ExpandableButton
                                    dataTestId={DataTestId.PRODUCT_SNIPPET_CHECKOUT_BUTTON}
                                    color="searchRed"
                                    onClick={onCheckoutClickHandler}
                                    aria-label={t('Купить')}
                                    role="button"
                                >
                                    <IconPurchases16 size={16} />
                                    <ExpandableButtonText>{t('Купить')}</ExpandableButtonText>
                                </ExpandableButton>
                            </ExpandableButtonWrapper>
                        </RelativeContainer>
                    ) : null}
                </Flex>
                {Boolean(showSiteButton && post && siteButtonText) && (
                    <Box width="100%">
                        {withCheckout ? (
                            <PostTextSiteLink
                                post={post!}
                                text={t(CHECKOUT_BUTTON_TEXT)}
                                onClick={onCheckoutClickHandler}
                                color="searchRed"
                                addonBefore={<IconPurchases16 size={16} />}
                            />
                        ) : (
                            <PostTextSiteLink
                                post={post!}
                                text={siteButtonText!}
                                onClick={onClick}
                                addonAfter={<IconArrowDiagonalRight size={16} />}
                            />
                        )}
                    </Box>
                )}
            </Flex>
        </OverlayLink>
    );
};
